import React, { memo, useCallback, useMemo, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';

import { DateUtils, Utils, Container } from '../../components';
import { COLOR, FONT, THEME } from '../../../constants/styles';

const EMAIL_HEADER_IMAGE = require('../../../../resources/email-header.png');
const PREVIEW_TIME = '07:30';

const EMPTY_MESSAGE_BY_CHANNEL = {
  email: 'No custom email message configured.',
  sms: 'No custom SMS message configured.',
};

const trimText = (value) => value?.trim();

const getTomorrowPreviewDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return DateUtils.formatDateString(tomorrow);
};

const replacePreviewTokens = (value) => {
  if (typeof value !== 'string') return value;

  return value.replace('${DATE}', getTomorrowPreviewDate()).replace('${TIME}', PREVIEW_TIME);
};

const getScopedRuleLabel = (item) => {
  return [
    item?.appointmentTypeLabel || 'All appointment types',
    item?.masterDepartmentLabel || 'All departments',
    item?.reasonLabel || 'Any reason',
  ].join(' • ');
};

const MessageQuote = memo(function MessageQuote({
  channel = 'email',
  containerStyle,
  text,
  testID,
  textStyle,
}) {
  const messageText =
    trimText(replacePreviewTokens(text)) ||
    EMPTY_MESSAGE_BY_CHANNEL[channel] ||
    EMPTY_MESSAGE_BY_CHANNEL.email;

  return (
    <View style={[styles.quoteContainer, containerStyle]}>
      <Text style={[styles.quoteText, textStyle]} testID={testID}>
        “{messageText}”
      </Text>
    </View>
  );
});

const EmailFooter = memo(function EmailFooter() {
  return (
    <View style={styles.footerContainer}>
      <View style={styles.footerIconRow}>
        <View style={styles.footerIcon} />
        <View style={styles.footerIcon} />
        <View style={styles.footerIcon} />
        <View style={styles.footerIcon} />
        <View style={styles.footerIcon} />
      </View>
    </View>
  );
});

const BaseLogo = memo(function BaseLogo({ employerId, testID }) {
  const [hasLogoLoadError, setHasLogoLoadError] = useState(false);
  const employerLogoUri = useMemo(() => {
    if (!employerId || hasLogoLoadError) return null;

    return Utils.employerImagePath(employerId);
  }, [employerId, hasLogoLoadError]);
  const handleLogoError = useCallback(() => {
    setHasLogoLoadError(true);
  }, []);

  if (!employerLogoUri) {
    return (
      <View style={styles.logoFallback} testID={testID}>
        <Text style={styles.logoFallbackText}>OP</Text>
      </View>
    );
  }

  return (
    <Image
      resizeMode="contain"
      source={{ uri: employerLogoUri }}
      style={styles.logoImage}
      onError={handleLogoError}
      testID={testID}
    />
  );
});

const EmailSkeletonCard = memo(function EmailSkeletonCard({
  employerId,
  title,
  type,
  messageText,
  testID,
}) {
  return (
    <View style={styles.previewSection} testID={testID}>
      <Text style={styles.previewSectionTitle}>{title}</Text>
      <View style={styles.emailFrame}>
        <View style={styles.emailHeaderImageContainer}>
          <Image resizeMode="contain" source={EMAIL_HEADER_IMAGE} style={styles.emailHeaderImage} />
        </View>
        <View style={styles.emailBody}>
          <BaseLogo
            employerId={employerId}
            testID={`scheduler-notification-preview-${type}-base-logo`}
          />

          <MessageQuote
            channel="email"
            text={messageText}
            testID={
              type === 'confirmation'
                ? 'scheduler-notification-preview-confirmation-message'
                : 'scheduler-notification-preview-unavailable-message'
            }
          />
        </View>
        <EmailFooter />
      </View>
    </View>
  );
});

const SmsPreviewCard = memo(function SmsPreviewCard({ title, type, messageText, testID }) {
  return (
    <View style={styles.previewSection} testID={testID}>
      <Text style={styles.previewSectionTitle}>{title}</Text>
      <View style={styles.smsFrame}>
        <View style={styles.smsHeader}>
          <Text style={styles.smsHeaderTitle}>Text message</Text>
        </View>

        <View style={styles.smsConversationContainer}>
          <View style={styles.smsBubble}>
            <MessageQuote
              channel="sms"
              containerStyle={styles.smsQuoteContainer}
              text={messageText}
              testID={
                type === 'confirmation'
                  ? 'scheduler-notification-preview-sms-confirmation-message'
                  : 'scheduler-notification-preview-sms-unavailable-message'
              }
              textStyle={styles.smsQuoteText}
            />
          </View>
        </View>
      </View>
    </View>
  );
});

export default memo(function SchedulerNotificationPreviewModal({ employerId, item }) {
  const scopedRuleLabel = useMemo(() => getScopedRuleLabel(item), [item]);

  return (
    <Container scrollView={true} testID="scheduler-notification-preview-modal">
      <View style={styles.modalHeader}>
        <Text style={[THEME.textMedium, { textAlign: 'center' }]}>{scopedRuleLabel}</Text>
      </View>

      <View style={styles.previewsRow}>
        <EmailSkeletonCard
          employerId={employerId}
          title="Confirmation email"
          type="confirmation"
          messageText={item?.emailConfirmation}
          testID="scheduler-notification-preview-confirmation-card"
        />
        <EmailSkeletonCard
          employerId={employerId}
          title="Unavailable email"
          type="unavailable"
          messageText={item?.emailUnavailable}
          testID="scheduler-notification-preview-unavailable-card"
        />
        <SmsPreviewCard
          title="Confirmation SMS"
          type="confirmation"
          messageText={item?.smsConfirmation}
          testID="scheduler-notification-preview-sms-confirmation-card"
        />
        <SmsPreviewCard
          title="Unavailable SMS"
          type="unavailable"
          messageText={item?.smsUnavailable}
          testID="scheduler-notification-preview-sms-unavailable-card"
        />
      </View>
    </Container>
  );
});

const styles = StyleSheet.create({
  modalHeader: {
    paddingTop: 20,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: COLOR.altAccent,
  },
  previewsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingVertical: 35,
  },
  previewSection: {
    width: 500,
    maxWidth: '100%',
    marginHorizontal: 20,
    marginBottom: 24,
  },
  previewSectionTitle: {
    ...THEME.textSemibold,
    marginBottom: 10,
  },
  smsFrame: {
    backgroundColor: '#f3f6fb',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#d9e1ee',
    overflow: 'hidden',
  },
  smsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    backgroundColor: '#eef2f8',
    borderBottomWidth: 1,
    borderBottomColor: '#d9e1ee',
  },
  smsHeaderTitle: {
    ...FONT.semibold,
    color: COLOR.text,
    fontSize: 14,
  },
  smsHeaderTime: {
    ...FONT.regular,
    color: COLOR.textLight,
    fontSize: 12,
  },
  smsConversationContainer: {
    padding: 18,
    backgroundColor: '#f8fbff',
  },
  smsBubble: {
    alignSelf: 'flex-start',
    maxWidth: '88%',
    backgroundColor: '#ddf5c8',
    borderRadius: 18,
    borderBottomLeftRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 6,
  },
  smsQuoteContainer: {
    backgroundColor: 'transparent',
    borderLeftWidth: 0,
    paddingHorizontal: 0,
    paddingVertical: 0,
    marginBottom: 0,
  },
  emailFrame: {
    backgroundColor: '#eaeced',
    borderRadius: 10,
    padding: 18,
  },
  emailHeaderImageContainer: {
    overflow: 'hidden',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    height: 90,
  },
  emailHeaderImage: {
    width: '100%',
    aspectRatio: 750 / 110,
  },
  emailBody: {
    backgroundColor: '#f9f9f9',
    paddingHorizontal: 30,
    paddingTop: 26,
    paddingBottom: 28,
    alignItems: 'center',
  },
  logoFallback: {
    width: 120,
    height: 84,
    borderRadius: 10,
    backgroundColor: COLOR.button,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 22,
  },
  logoFallbackText: {
    ...FONT.semibold,
    color: COLOR.contrast,
    fontSize: 28,
  },
  logoImage: {
    width: 60,
    height: 60,
    marginBottom: 22,
  },
  quoteContainer: {
    maxWidth: 620,
    backgroundColor: COLOR.altContrast,
    borderLeftWidth: 4,
    borderLeftColor: COLOR.button,
    paddingHorizontal: 16,
    paddingVertical: 14,
    marginBottom: 22,
  },
  smsQuoteText: {
    textAlign: 'left',
  },
  quoteText: {
    ...FONT.regular,
    color: COLOR.text,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
  },
  footerContainer: {
    backgroundColor: '#eaeced',
    paddingTop: 16,
    paddingBottom: 8,
    alignItems: 'center',
  },
  footerIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  footerIcon: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: COLOR.textLight,
    opacity: 0.55,
    marginHorizontal: 6,
  },
});
